import { cva, type VariantProps } from 'class-variance-authority';
import { CircleAlert, CircleCheck, CircleX, Info, Sparkles, type LucideIcon } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import type { Tone } from './badge';
import { cn } from './cn';

const alertVariants = cva(
  'flex items-start gap-3 rounded-card px-4 py-3 text-sm [&>svg]:mt-0.5 [&>svg]:size-icon [&>svg]:shrink-0',
  {
    variants: {
      tone: {
        ok: 'bg-ok-wash text-ok',
        warn: 'bg-warn-wash text-warn',
        err: 'bg-err-wash text-err',
        info: 'bg-info-wash text-info',
        neutral: 'bg-neutral-wash text-neutral',
        accent: 'bg-accent-wash text-accent-text',
      },
    },
    defaultVariants: { tone: 'info' },
  },
);

const ALERT_ICONS: Readonly<Record<Tone, LucideIcon>> = {
  ok: CircleCheck,
  warn: CircleAlert,
  err: CircleX,
  info: Info,
  neutral: Info,
  accent: Sparkles,
};

export type AlertProps = Omit<ComponentProps<'div'>, 'title'> &
  VariantProps<typeof alertVariants> & {
    readonly title?: ReactNode;
    readonly icon?: LucideIcon | false;
    /** Ação à direita (botão "Tentar de novo", link). */
    readonly action?: ReactNode;
  };

/** Aviso em wash + texto. Erro e atenção anunciam (`role="alert"`); os outros são `status`. */
export function Alert({ className, tone, title, icon, action, children, role, ...props }: AlertProps) {
  const resolved = tone ?? 'info';
  const Icon = icon === false ? null : (icon ?? ALERT_ICONS[resolved]);
  return (
    <div
      role={role ?? (resolved === 'err' || resolved === 'warn' ? 'alert' : 'status')}
      data-slot="alert"
      data-tone={resolved}
      className={cn(alertVariants({ tone }), className)}
      {...props}
    >
      {Icon ? <Icon aria-hidden /> : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title ? 'mt-0.5' : null)}>{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
